import { memo } from 'react'
import { Card, CardContent, CardActionArea, Box, Typography, useTheme } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

/**
 * QuickActionCard component - Reusable card for quick actions on dashboards
 * 
 * @param {Object} props
 * @param {string} props.title - Title of the action
 * @param {string} props.description - Optional description/subtitle
 * @param {ReactNode} props.icon - Icon to display
 * @param {Function} props.onClick - Click handler
 * @param {string} props.color - Color variant ('primary', 'secondary', 'success', 'warning', 'error', 'info')
 * @param {boolean} props.disabled - Whether the action is disabled
 * @param {Object} props.sx - Additional sx styles
 */
export const QuickActionCard = memo(({
  title,
  description,
  icon,
  onClick,
  color = 'primary',
  disabled = false,
  sx = {},
}) => {
  const theme = useTheme()
  const palette = theme.palette[color] || theme.palette.primary
  const accentColor =
    theme.palette.mode === 'light'
      ? color === 'warning'
        ? theme.palette.text.primary
        : palette.dark
      : palette.main

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
        cursor: disabled ? 'not-allowed' : 'pointer',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)',
          borderColor: `${color}.main`,
        },
        ...(disabled && {
          opacity: 0.6,
          '&:hover': {
            transform: 'none',
            boxShadow: 2,
          },
        }),
        ...sx,
      }}
      onClick={disabled ? undefined : onClick}
    >
      <CardContent
        sx={{
          height: '100%',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          '&:last-child': {
            pb: 3,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}.light`,
              color: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              color: 'text.secondary',
              transition: 'all 0.2s',
              '&:hover': {
                color: accentColor,
                bgcolor: `${color}.light`,
              },
            }}
          >
            <ArrowForwardIcon fontSize="small" />
          </Box>
        </Box>
        
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: description ? 0.5 : 0,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        
        {description && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
})

QuickActionCard.displayName = 'QuickActionCard'
